import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";

import { campaignJobBoardQueryKeys } from "./queryKeys";

import { clientQueryKeys } from "@/features/clients/api/query-keys";
import { invoiceQueryKeys } from "@/features/invoices/api/queryKeys";
import { instance } from "@/lib/axios/instance";
import { ProblemDetails } from "@/lib/helpers/handleProblemDetailsError";
import { CampaignJobBoardType } from "@/types/response/CampaignJobBoardType";
import { CampaignTypeDTO } from "@/types/response/CampaignType";
import { PagedResponse } from "@/types/response/PagedResponse";

interface UpdateMonthBudgetParams {
  campaignId: string;
  clientId: string;
  clientIntegrationModuleId: string;
  year: string;
  month: string;
  amount: number;
}

interface MutationContext {
  previousDetail: CampaignJobBoardType | undefined;
  previousMonths: [
    readonly unknown[],
    PagedResponse<CampaignTypeDTO> | undefined,
  ][];
}

export function useUpdateCampaignJobBoardMonthBudgetMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse,
    AxiosError<ProblemDetails>,
    UpdateMonthBudgetParams,
    MutationContext
  >({
    mutationFn: ({ campaignId, clientIntegrationModuleId, clientId, year, month, amount }) => {
      return instance.put(
        `/api/v2/Campaigns/${campaignId}/Integrations/${clientIntegrationModuleId}/Budget/Month/${year}/${month}`,
        { clientId2: clientId, amount },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    },
    onMutate: async ({
      campaignId,
      clientIntegrationModuleId,
      clientId,
      year,
      month,
      amount,
    }) => {
      const detailKey = campaignJobBoardQueryKeys.detail(
        campaignId,
        clientIntegrationModuleId,
        clientId
      );
      const monthsKey = campaignJobBoardQueryKeys.months.all;
      const monthString = `${year}-${month.padStart(2, "0")}`;

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await Promise.all([
        queryClient.cancelQueries({ queryKey: detailKey }),
        queryClient.cancelQueries({ queryKey: monthsKey }),
      ]);

      // Snapshot the previous values
      const previousDetail =
        queryClient.getQueryData<CampaignJobBoardType>(detailKey);
      const previousMonths = queryClient.getQueriesData<
        PagedResponse<CampaignTypeDTO>
      >({ queryKey: monthsKey });

      if (previousDetail) {
        const optimisticDetail: CampaignJobBoardType = {
          ...previousDetail,
          monthlyRecords: previousDetail.monthlyRecords?.map((record) => {
            if (!record.month.startsWith(monthString)) return record;
            // A prorated month is edited through its monthly budget; the prorated allocation is
            // recalculated server-side and arrives with the refetch.
            if (record.monthlyBudget !== null) {
              return { ...record, monthlyBudget: amount };
            }
            return { ...record, budget: amount };
          }),
        };

        queryClient.setQueryData<CampaignJobBoardType>(
          detailKey,
          optimisticDetail
        );
      }

      // Optimistically update the campaign months cache (budget planning page reads this).
      // The list is already scoped to the selected month, so match on campaign +
      // client integration module and patch the selected month's budget in place.
      queryClient.setQueriesData<PagedResponse<CampaignTypeDTO>>(
        { queryKey: monthsKey },
        (current) =>
          current
            ? {
              ...current,
              data: current.data.map((campaign) =>
                campaign.id === campaignId
                  ? {
                    ...campaign,
                    integrations: campaign.integrations?.map(
                      (integration) => {
                        const cimId =
                              integration.selectedMonth
                                ?.clientIntegrationModuleId ??
                              integration.clientIntegrationModuleId;
                        return cimId === clientIntegrationModuleId &&
                              integration.selectedMonth
                          ? {
                            ...integration,
                            selectedMonth: {
                              ...integration.selectedMonth,
                              budget: amount,
                            },
                          }
                          : integration;
                      }
                    ),
                  }
                  : campaign
              ),
            }
            : current
      );

      // Return a context object with the snapshotted values
      return { previousDetail, previousMonths };
    },
    onError: (_error, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousDetail) {
        const detailKey = campaignJobBoardQueryKeys.detail(
          variables.campaignId,
          variables.clientIntegrationModuleId,
          variables.clientId
        );
        queryClient.setQueryData<CampaignJobBoardType>(
          detailKey,
          context.previousDetail
        );
      }

      context?.previousMonths?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: async (_data, _error, variables) => {
      const { campaignId, clientIntegrationModuleId, clientId } = variables;
      const detailKey = campaignJobBoardQueryKeys.detail(
        campaignId,
        clientIntegrationModuleId,
        clientId
      );
      
      // Force refetch by invalidating with refetchType: 'all' and then explicitly refetching
      // This ensures the query refetches even if it has staleTime
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: campaignJobBoardQueryKeys.details(),
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: detailKey,
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: campaignJobBoardQueryKeys.lists(),
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: campaignJobBoardQueryKeys.list(campaignId, clientId),
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: campaignJobBoardQueryKeys.months.all,
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: invoiceQueryKeys.cache.transactions.byClient(clientId),
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: clientQueryKeys.detail(clientId),
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: campaignJobBoardQueryKeys.clientBudgetMonth.byClient(clientId),
          refetchType: "all",
        }),
      ]);

      // Explicitly refetch to ensure immediate update (handles staleTime and month format mismatch)
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: detailKey,
          type: "active",
        }),
        queryClient.refetchQueries({
          queryKey: campaignJobBoardQueryKeys.clientBudgetMonth.byClient(clientId),
          type: "active",
        }),
        queryClient.refetchQueries({
          queryKey: clientQueryKeys.detail(clientId),
          type: "active",
        }),
      ]);
    },
  });
}
